import { useState } from 'react';
import type { RuntimeFilterOp } from '@thoughtspot/visual-embed-sdk/react';
import { HostEvent, LiveboardEmbed, useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { FILTER_STYLES } from 'shared/constants';
import { InfoBox } from 'shared/ui/InfoBox';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { logError } from 'shared/lib/log-errors';
import { useGetUser } from 'entities/profile';
import type { AuthenticatedContractorModel } from 'entities/profile';
import classNames from 'classnames';

import type { EmbedTabs } from '../ReportTabs';
import { ReportTabs } from '../ReportTabs';
import type { NotAvailableBackgroundType } from '../NotAvailable';
import { NotAvailableBackground, NotAvailableModal } from '../NotAvailable';

interface CommonLiveboardProps {
  filters: JSX.Element;
  title: string;
  liveBoardId: string;
  runtimeFilters: {
    columnName: string;
    operator: RuntimeFilterOp;
    values: string[];
  }[];
}

type PrimaryLiveboardProps = {
  type: 'primary';
};

type InfoboxLiveboardProps = {
  type: 'infobox';
  shouldLoadLiveboard: boolean;
  infoboxTitleText: string;
  infoboxText: string;
};

type DispositionLiveboardProps = {
  type: 'disposition';
  notAvailableBackgroundType: NotAvailableBackgroundType;
};

type InfoboxDispositionLiveboardProps = {
  type: 'infobox-disposition';
  shouldLoadLiveboard: boolean;
  infoboxTitleText: string;
  infoboxText: string;
  notAvailableBackgroundType: NotAvailableBackgroundType;
};

type LiveboardEmbedConstructorProps = CommonLiveboardProps &
  (PrimaryLiveboardProps | InfoboxLiveboardProps | DispositionLiveboardProps | InfoboxDispositionLiveboardProps);

export function LiveboardEmbedConstructor(props: LiveboardEmbedConstructorProps) {
  const { type, title, filters, liveBoardId, runtimeFilters } = props;

  const embedRef = useEmbedRef<typeof LiveboardEmbed>();
  const [embedTabs, setEmbedTabs] = useState<EmbedTabs>();

  const userData = useGetUser<AuthenticatedContractorModel>().data?.data;
  const isDispositionData = userData?.hasThoughtspotDispositionData;

  const onLiveboardRendered = () => {
    if (!embedTabs) {
      embedRef.current.trigger(HostEvent.GetTabs).then(setEmbedTabs).catch(logError);
    }
  };

  return (
    <div>
      <div className="relative">
        {!isDispositionData && (type === 'disposition' || type === 'infobox-disposition') ? (
          <NotAvailableModal />
        ) : null}
      </div>
      <div
        className={classNames('flex justify-between px-[25px] pt-5', {
          'blur-sm pointer-events-none select-none':
            !isDispositionData && (type === 'disposition' || type === 'infobox-disposition'),
        })}
      >
        <div className="mt-[6px] flex items-start pr-4 text-lg font-semibold">{title}</div>
        <div className={FILTER_STYLES.container}>{filters}</div>
      </div>
      <ReportTabs
        className="ml-6 mt-1"
        embedRef={embedRef}
        embedTabs={embedTabs}
      />
      {!isDispositionData && (type === 'infobox-disposition' || type === 'disposition') ? (
        <NotAvailableBackground type={props.notAvailableBackgroundType} />
      ) : null}
      {(isDispositionData && type === 'infobox-disposition' && !props.shouldLoadLiveboard) ||
      (type === 'infobox' && !props.shouldLoadLiveboard) ? (
        <InfoBox
          title={
            <div className="flex items-center">
              <MaterialIcon className="mr-2">insert_chart</MaterialIcon>
              {props.infoboxTitleText}
            </div>
          }
          text={props.infoboxText}
          className="m-6 bg-white py-12"
        />
      ) : null}
      {(type === 'infobox-disposition' && isDispositionData && props.shouldLoadLiveboard) ||
      (type === 'disposition' && isDispositionData) ||
      (type === 'infobox' && props.shouldLoadLiveboard) ||
      type === 'primary' ? (
        <LiveboardEmbed
          ref={embedRef}
          onLiveboardRendered={onLiveboardRendered}
          className="mt-1"
          liveboardId={liveBoardId}
          hideLiveboardHeader
          frameParams={{ height: '75vh' }}
          runtimeFilters={runtimeFilters}
          visibleActions={[]}
          fullHeight
        />
      ) : null}
    </div>
  );
}
